<template>
	<div :class="classes">
		<div class="message-list--item-content">
			<div v-if="computedIcon" class="message-list--item-icon-container">
				<Icon :icon="computedIcon"/>
			</div>
			<div v-else-if="isRecipient && !info" class="message-list--item-icon-container">
				<Jon white/>
			</div>
			<div class="message-list--item-content-inner">
				<template v-if="hasTextSlot">
					<slot name="text"/>
				</template>
				<template v-else>
					<span v-if="customText" v-html="customText"/>
					<span v-html="text"/>
				</template>
				<div
					v-if="showProfileInfo"
					class="mt-3"
				>
					<Button
						text="skilldoo-Profil ansehen"
						icon="caret-right"
						@click="$emit('clickedMoreInfos')"
					/>
				</div>
				<div v-if="showAppointmentInfo">
					<AppointmentInfosLight
						:data="messageExtraData"
					/>
					<Button
						v-if="isLatestMessage && isInitiatorCustomer"
						text="Termine öffnen"
						icon="caret-right"
						class="mt-3"
						@click="openAppointments"
					/>
				</div>
				<div v-if="showAppointmentInfoConfirmed">
					<AppointmentInfosConfirmed
						:message="message"
						:data="messageExtraData"
						@editAccessLink="$emit('editAccessLink')"
					/>
				</div>
				<div v-if="showCollaborationText" v-html="collaborationText" class="mt-2"/>
			</div>
		</div>
		<div v-if="showSender" class="message-list--item-sender">
			<span v-if="senderName">{{ senderName }}</span>
			<span v-if="timeStamp">{{ timeStamp | dateTime }} Uhr</span>
		</div>
	</div>
</template>

<script>
import Icon from "@/vue/icon/Icon.vue";
import Jon from "@/vue/common/image/Jon.vue";
import * as ConversationStatus from "@/helper/communication/conversation/Status";
import Button from "@/vue/common/Button.vue";
import AppointmentInfosLight from "@/vue/communication/conversation/message/reply/AppointmentInfosLight.vue";
import AppointmentInfosConfirmed from "@/vue/communication/conversation/message/reply/AppointmentInfosConfirmed.vue";
import {mapActions} from "vuex";
import {LIST_TYPE__CUSTOMER} from "@/store/communication/appointment";
import {INITIATOR_CUSTOMER} from "@/store/communication/conversation/messages";
import {get} from "@/helper/communication/conversation/message/CollaborationText";

const ICON_RECIPIENT = {};

const ICON_NOT_RECIPIENT = {
	[ConversationStatus.APPLICANT_IS_INTERESTED]: 'checkmark',
	[ConversationStatus.APPLICANT_IS_INTERESTED_TOO]: 'checkmark',
	[ConversationStatus.CUSTOMER_IS_INTERESTED]: 'checkmark',
	[ConversationStatus.CUSTOMER_IS_INTERESTED_TOO]: 'checkmark',
	[ConversationStatus.CUSTOMER_REQUESTED_VITA]: 'checkmark',
	[ConversationStatus.APPLICANT_DECLINED_VITA]: 'checkmark',
	[ConversationStatus.APPLICANT_SENT_VITA]: 'checkmark',
	[ConversationStatus.CUSTOMER_SENT_APPOINTMENT_TO_APPLICANT]: 'checkmark',
	[ConversationStatus.APPLICANT_REQUESTED_NEW_APPOINTMENT_DATES]: 'checkmark',
	[ConversationStatus.APPLICANT_APPOINTMENT_DATE_COUNTER_PROPOSAL]: 'checkmark',
	[ConversationStatus.CUSTOMER_DEFERRED_APPOINTMENT]: 'checkmark',
	[ConversationStatus.SEND_APPOINTMENT_ACCESS_LINK]: 'checkmark',
	[ConversationStatus.APPLICANT_DEFERRED_APPOINTMENT]: 'checkmark',
	[ConversationStatus.APPLICANT_INTERESTED_IN_COLLABORATION]: 'checkmark',
	[ConversationStatus.APPLICANT_INTERESTED_IN_COLLABORATION_WANTS_TO_WAIT]: 'checkmark',
	[ConversationStatus.COLLABORATION]: 'checkmark',
	[ConversationStatus.ALTERNATIVE_APPOINTMENT_POSSIBLE_FOR_CUSTOMER]: 'checkmark',
	[ConversationStatus.APPLICANT_WANTS_ALTERNATIVE_APPOINTMENT]: 'checkmark',
	[ConversationStatus.FOLLOW_UP_APPOINTMENT]: 'checkmark',
	[ConversationStatus.COLLABORATION_ACCEPT]: 'checkmark',
	[ConversationStatus.SENT_OFF_BY_APPLICANT]: 'checkmark',
	[ConversationStatus.SENT_OFF_BY_CUSTOMER]: 'checkmark',
};

const NO_SENDER_NAME_STATUS_LIST = [
	ConversationStatus.APPOINTMENT_CONFIRMED
];

export default {
	components: {
		AppointmentInfosLight, AppointmentInfosConfirmed, Button, Jon, Icon
	},

	props: {
		isRecipient: {
			type: Boolean,
			required: false,
		},
		info: {
			type: Boolean,
			required: false,
			default: false,
		},
		message: {
			type: Object,
			required: false,
		},
		icon: {
			type: String,
			required: false,
		},
		customText: {
			type: String,
			required: false,
		},
		customTimeStamp: {
			type: Date,
			required: false,
		},
		forceRight: {
			type: Boolean,
			required: false,
			default: false
		},
		isLatestMessage: {
			type: Boolean,
			required: false,
			default: false,
		},
		initiator: {
			type: String,
			required: false
		},
		forceSenderDisplay: {
			type: Boolean,
			required: false,
			default: false
		},
	},

	computed: {
		hasTextSlot ()
		{
			return !!this.$slots.text;
		},
		showProfileInfo ()
		{
			return this.message
				&& this.message.messageConversationStatus.id === ConversationStatus.APPLICANT_IS_INTERESTED
				&& this.isRecipient;
		},
		showAppointmentInfo ()
		{
			return this.message
				&& this.message.extraData
				&& [
					ConversationStatus.CUSTOMER_SENT_APPOINTMENT_TO_APPLICANT,
					ConversationStatus.CUSTOMER_DEFERRED_APPOINTMENT,
					ConversationStatus.APPLICANT_APPOINTMENT_DATE_COUNTER_PROPOSAL,
					ConversationStatus.APPLICANT_DEFERRED_APPOINTMENT,
					ConversationStatus.CUSTOMER_DEFERRED_APPOINTMENT,
					ConversationStatus.FOLLOW_UP_APPOINTMENT,
				].includes(this.message.messageConversationStatus.id);
		},
		showAppointmentInfoConfirmed ()
		{
			return this.message
				&& this.message.extraData
				&& [
					ConversationStatus.APPOINTMENT_CONFIRMED
				].includes(this.message.messageConversationStatus.id);
		},
		showCollaborationText ()
		{
			return this.message
				&& !this.isRecipient
				&& [
					ConversationStatus.COLLABORATION
				].includes(this.message.messageConversationStatus.id);
		},
		collaborationText ()
		{
			return get(this.message);
		},
		isInitiatorCustomer ()
		{
			return this.initiator === INITIATOR_CUSTOMER;
		},
		computedIcon ()
		{
			if (this.icon)
			{
				return this.icon;
			}

			if (this.info)
			{
				return undefined;
			}

			return this.iconByMessage;
		},
		messageExtraData ()
		{
			return this.message?.extraData
				? JSON.parse(this.message.extraData)
				: undefined;
		},
		iconByMessage ()
		{
			if (!this.message)
			{
				return undefined;
			}

			if (this.isRecipient)
			{
				return ICON_RECIPIENT[this.message.messageConversationStatus.id];
			}

			return ICON_NOT_RECIPIENT[this.message.messageConversationStatus.id];
		},
		classes ()
		{
			return {
				'message-list--item': true,
				'message-list--item-recipient': this.isRecipient,
				'message-list--item-info': this.info,
				'message-list--item-right': this.forceRight
			};
		},
		showSender ()
		{
			if (this.forceSenderDisplay)
			{
				return true;
			}

			return this.message?.messageConversationStatus.id !== ConversationStatus.CUSTOMER_REQUESTED_VITA;
		},
		senderName ()
		{
			if (!this.message || NO_SENDER_NAME_STATUS_LIST.includes(this.message.messageConversationStatus.id))
			{
				return undefined;
			}

			if (
				this.message.recipientType.customer
				&& this.message.disclosedData
			)
			{
				return this.message.disclosedData.name;
			}

			if (
				this.message.recipientType.applicant
				&& this.message.customer
			)
			{
				return this.message.customer.name;
			}

			return undefined;
		},
		timeStamp ()
		{
			return this.message
				? this.message.timeStamp
				: this.customTimeStamp;
		},
		text ()
		{
			if (this.message)
			{
				if (!this.isRecipient)
				{
					return this.message.textChatOpposite;
				}

				return this.message.textChat || this.message.subject;
			}

			return '';
		},
	},

	methods: {
		...mapActions('communication/appointment', {
			showAppointmentFan: 'show'
		}),
		openAppointments ()
		{
			this.showAppointmentFan({
				listType: LIST_TYPE__CUSTOMER,
				onClose: () =>
				{
					this.$emit('possibleChange');
				}
			});
		}
	}
}
</script>
